import React, { useState } from 'react';

import Dialog from 'ui/views/dialogs/Dialog';
import { ICompany } from 'types/company';
import Button from 'ui/elements/buttons/Button';
import { CommunityUpdateVisibility, CompanyUpdateVisibility } from 'types/content';
import Resource from 'util/resource/Resource';
import { FeedType, PostTo } from './types';
import { VisibilitySelector } from './Visibility/VisibilitySelector';
import { flatten } from 'ramda';
import { TinyCommunity } from 'types/company/community';
import { notEmpty } from 'util/arrayUtils';
import PostCommunityUpdateDialog from '../community/PostCommunityUpdateDialog';
import { EmbeddedUpdate } from 'ui/elements/form/RichTextEditor/components/UpdateBlot';
import { usePostableSources } from 'apis/CompanyAPI/users/usePostableSources';
import PostUpdateDialog from '../company/PostUpdateDialog';
import { companyUpdateAPIurls } from 'apis/CompanyAPI/content/UpdateAPI';
import { COMPANY_INITIAL_PAGINATION } from 'pages/Company/Overview/sections/updates';
import { invalidate } from 'hooks/useSWR';

export type PostableFeedBase<T extends FeedType> = T extends 'company'
  ? { type: T; profile: ICompany; visibilityOptions: CompanyUpdateVisibility[] }
  : T extends 'community'
    ? { type: T; profile: TinyCommunity; visibilityOptions: CommunityUpdateVisibility[] }
    : never;

export type PostableCompanyFeed = PostableFeedBase<'company'>;
export type PostableCommunityFeed = PostableFeedBase<'community'>;
export type PostableFeed = PostableCompanyFeed | PostableCommunityFeed;

export default function RepostUpdateDialog({
  isOpen,
  embeddedUpdate,
  onClose,
}: {
  isOpen: boolean;
  embeddedUpdate: EmbeddedUpdate;
  onClose: () => void;
}) {
  const { resource: postableSourcesResource } = usePostableSources();

  const [selectedFeed, setSelectedFeed] = useState<PostTo>();
  const [isNextClicked, setIsNextClicked] = useState(false);

  if (!isNextClicked) {
    return (
      <Dialog open={isOpen} onClose={onClose}>
        <Resource resource={postableSourcesResource}>
          {postableSourcesData => {
            const postableFeeds = postableSourcesData.postableFeeds;
            const companies = postableFeeds.filter(feed => feed.type === 'company');
            const companyOptions: (PostTo | string)[] = flatten(
              postableFeeds
                .filter(feed => feed.type === 'company')
                .map(feed =>
                  flatten(
                    [
                      companies.length > 1 ? feed.profile.name : undefined,
                      feed.visibilityOptions.map(visibility => ({
                        type: 'company' as const,
                        visibility,
                        profile: feed.profile,
                      })),
                    ].filter(notEmpty),
                  ),
                ),
            );
            const communities: PostTo[] = flatten(
              postableFeeds
                .filter(feed => feed.type === 'community')
                .map(feed =>
                  feed.visibilityOptions.map(visibility => ({
                    type: 'community' as const,
                    visibility,
                    profile: feed.profile,
                  })),
                ),
            );

            return (
              <>
                <VisibilitySelector
                  value={selectedFeed}
                  options={[
                    ...companyOptions,
                    communities.length > 0 ? 'Communities you are part of:' : undefined,
                    ...communities,
                  ].filter(notEmpty)}
                  onChange={setSelectedFeed}
                  onClose={onClose}
                  confirmAction={
                    <Button disabled={!selectedFeed} onClick={() => setIsNextClicked(true)} kind="primary">
                      Next
                    </Button>
                  }
                />
              </>
            );
          }}
        </Resource>
      </Dialog>
    );
  }

  switch (selectedFeed?.type) {
    case 'company':
      return (
        <PostUpdateDialog
          isOpen={isOpen}
          onClose={() => {
            onClose();
            setSelectedFeed(undefined);
            setIsNextClicked(false);
          }}
          company={selectedFeed.profile}
          embeddedUpdate={embeddedUpdate}
          onUpdatePosted={() => {
            invalidate(companyUpdateAPIurls.list(selectedFeed.profile.id, false, COMPANY_INITIAL_PAGINATION));
          }}
          successMessage={`Posted to the ${selectedFeed.profile.name} feed!`}
          defaultVisibility={selectedFeed.visibility}
        />
      );
    case 'community':
      return (
        <PostCommunityUpdateDialog
          isOpen={isOpen}
          onClose={() => {
            onClose();
            setSelectedFeed(undefined);
            setIsNextClicked(false);
          }}
          community={selectedFeed.profile}
          onUpdatePosted={onClose}
          postTo={selectedFeed}
          embeddedUpdate={embeddedUpdate}
          successMessage={`Posted to the ${selectedFeed.profile.name} feed!`}
        />
      );
    default:
      return '';
  }
}
